import { useMutation } from "@apollo/client";
import { FormControl, IconButton, InputAdornment, InputLabel, OutlinedInput, TextField } from "@material-ui/core";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import background from "assets/images/login_photo.png";
import papsImg from "assets/images/logo-bleu.svg";
import papsChristmasLogo from "assets/images/Paps_Noel.svg";
import { saveCurrentUserToken } from "config/auth";
import { GOOGLE_CLIENT_ID } from "config/constants";
import useText from "constants/text";
import { GoogleIcon } from "icons";
import React from "react";
import { GoogleLoginResponse, GoogleLoginResponseOffline, useGoogleLogin } from "react-google-login";
import { useNavigate } from "react-router-dom";
import "styled-components/macro";
import { Button, ButtonLink } from "ui/Button";
import { ValidateEmail } from "utils";
import { Authentification, ConnectionWithGoogleClient } from "../graphql/mutations";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(0),
      height: "100%",
      width: "100%",
      background: "#fff",
      display: "flex",
      justifyContent: "center",
      [theme.breakpoints.down("xs")]: {
        overflow: "hidden",
      },
      "@media(max-width: 768px)": {},
    },
    papsers: {
      width: "40%",
      backgroundImage: `url(${background})`,
      backgroundRepeat: "no-repeat",
      backgroundSize: "cover",
      "@media(max-width: 768px)": {
        display: "none",
      },
    },
    login: {
      width: "60%",
      marginLeft: "auto",
      marginRight: "auto",
      backgroundSize: "cover",
      "@media(max-width: 616px)": {
        width: "100%",
        display: "flex",
        justifyContent: "center",
      },
    },
    container: {
      marginLeft: "auto",
      marginRight: "auto",
      display: "flex",
      justifyContent: "center",
      justifyItems: "center",
      flexDirection: "column",
    },
    group: {
      marginLeft: "auto",
      marginRight: "auto",
      marginTop: "4%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    },
    imgDiv: {
      marginLeft: "auto",
      marginRight: "auto",
    },
    title: {
      paddingTop: "10%",
      marginLeft: "auto",
      marginRight: "auto",
      color: theme.palette.primary.main,
      padding: theme.spacing(2),
      fontWeight: 900,
      fontSize: 42,
    },
    form: {
      display: "flex",
      flexDirection: "column",
      "& .MuiTextField-root, .MuiFormControl-root ": {
        margin: theme.spacing(1),
        width: "45ch",
        backgroundColor: "#f2f2f2",
        borderRaduis: 4.7,
        "@media(max-width: 768px)": {
          width: "20rem",
        },
      },
      marginLeft: "auto",
      marginRight: "auto",
      paddingTop: "10%",
    },
    messagePassword: {
      width: "45ch",
      display: "flex",
      marginLeft: "auto",
      marginRight: "auto",
      justifyContent: "space-between",
      marginTop: 8,
      [theme.breakpoints.down("xs")]: {
        width: "20rem",
      },
    },
    buttonsLogin: {
      width: "45ch",
      display: "flex",
      marginLeft: "auto",
      marginRight: "auto",
      justifyContent: "space-between",
      marginTop: 8,
    },
    log: {
      background: theme.palette.primary.main,
      width: "14ch",
      color: "#fff",
      fontSize: 16.8,
      [theme.breakpoints.down("xs")]: {
        width: "20rem",
        marginLeft: "78.5px",
      },
    },
    pwd: {
      color: theme.palette.primary.main,
    },
    socials: {
      width: "45ch",
      display: "flex",
      marginTop: 10,
      justifyContent: "space-between",
      marginLeft: "auto",
      marginRight: "auto",
    },
    social: {
      borderRadius: 4.2,
      border: "solid 0.9px rgba(0, 0, 0, 0.1)",
      width: "57.0ch",
      marginTop: 30,
      marginBottom: "auto",
      display: "flex",
      fontSize: 12,
      color: "#9c9292",
      padding: 10,
      marginLeft: "auto",
      marginRight: "auto",
      cursor: "pointer",
      [theme.breakpoints.down("xs")]: {
        width: "20rem",
        marginTop: "12px",
        height: "6vh",
      },
    },
    viewError: {
      borderRadius: 4.2,
      width: "57.0ch",
      marginTop: 30,
      marginBottom: "auto",
      display: "flex",
      fontSize: 12,
      padding: 10,
      marginLeft: "auto",
      marginRight: "auto",
    },
    icon: {
      marginTop: "auto",
      marginBottom: "auto",
      padding: 5,
    },
    text: {
      marginTop: "auto",
      marginBottom: "auto",
    },
    error: {
      margin: "auto",
      marginTop: "auto",
      marginBottom: "auto",
      color: "red",
      paddingBottom: "0.7em",
    },
    field: {
      display: "flex",
      justifyContent: "center",
    },
  })
);
const defaultLogger = {
  email: "",
  password: "",
};
export function Login() {
  const styles = useStyles();
  const [logger, setLogger] = React.useState(defaultLogger);
  const [showPassword, setShowPassword] = React.useState(false);
  const navigate = useNavigate();
  const [authentification, { error }] = useMutation(Authentification);
  const [message, setMessage] = React.useState<string>("");
  const [googleAuthen] = useMutation(ConnectionWithGoogleClient);
  const [isFormSubmited, setFormSubmited] = React.useState(false);
  const logo = process.env.REACT_APP_PAPS_GIFT === "true" ? papsChristmasLogo : papsImg;
  const onFailure = () => {
    setMessage("Please login!");
  };

  const { text } = useText();

  const onSuccess = (res: GoogleLoginResponse | GoogleLoginResponseOffline) => {
    // @ts-ignore
    const token: string = res?.tokenId;
    // @ts-ignore
    const email: string = res?.profileObj?.email;

    googleAuthen({
      variables: {
        input: {
          token,
          user: {
            email,
          },
        },
      },
    })
      .then((response) => {
        const { data, message: msg, code } = response?.data?.clientConnectionWithGoogle;
        if (code === 200) {
          saveCurrentUserToken(data?.jwToken?.value, data?.jwToken?.expiration).then(() => {
            navigate("/");
            window.location.reload();
          });
        } else {
          setFormSubmited(false);
          setMessage(msg);
        }
      })
      .catch(() => {
        setFormSubmited(false);
        setMessage("You can't connect with this google account.");
      });
  };

  const { signIn } = useGoogleLogin({
    clientId: GOOGLE_CLIENT_ID,
    onFailure,
    onSuccess,
    isSignedIn: false,
    accessType: "offline",
    // onScriptLoadFailure: () => {},
  });
  const onChanged = (event: any) => {
    event.persist();
    setLogger((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  };
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handlerAuthentification = async (event: any) => {
    event.preventDefault();
    if (ValidateEmail(logger.email) === false) return;
    setFormSubmited(true);
    authentification({
      variables: {
        input: logger,
      },
    })
      .then((response) => {
        const { data, message: msg, code } = response.data?.clientAuthenticate;
        if (code === 200) {
          saveCurrentUserToken(data.jwToken.value, data.jwToken.expiration).then(() => {
            navigate("/");
            window.location.reload();
          });
        } else {
          setFormSubmited(false);
          setMessage(`${msg} Please try again.`);
        }
      })
      .catch(() => {
        setFormSubmited(false);
        setMessage("Password and/or email incorect.");
      });
  };

  return (
    <main className={styles.root}>
      <div className={styles.papsers} />
      <div className={styles.login}>
        <div className={styles.container}>
          <div className={styles.group}>
            <div className={styles.imgDiv}>
              <img
                src={logo}
                alt="paps icon"
                css={`
                  margin-left: auto;
                  margin-right: auto;
                `}
              />
            </div>
            <div className={styles.title}>Welcome</div>
          </div>
          <form className={styles.form} onSubmit={handlerAuthentification}>
            <div className={styles.error}>{message}</div>
            <div className={styles.field}>
              <TextField
                id="outlined-textarea"
                label="Email"
                placeholder="Email"
                variant="outlined"
                value={logger.email}
                type="email"
                onChange={onChanged}
                name="email"
                error={ValidateEmail(logger?.email) === false && logger?.email?.length !== 0}
                helperText={
                  ValidateEmail(logger?.email) === false && logger?.email?.length !== 0
                    ? "Please enter an valid email"
                    : ""
                }
                required
              />
            </div>
            <div className={styles.field}>
              <FormControl variant="outlined">
                <InputLabel htmlFor="outlined-adornment-password">Password</InputLabel>
                <OutlinedInput
                  id="outlined-adornment-password"
                  type={showPassword ? "text" : "password"}
                  value={logger.password}
                  name="password"
                  onChange={onChanged}
                  error={isFormSubmited && logger?.password?.length === 0}
                  required
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  }
                  labelWidth={70}
                />
              </FormControl>
            </div>
            <div className={styles.messagePassword}>
              <ButtonLink to="/forget/password" className={styles.pwd}>
                {text.all.forgot_password}
              </ButtonLink>
            </div>
            <div className={styles.buttonsLogin}>
              <Button className={styles.log} type="submit">
                Login
              </Button>
            </div>
            <div className={styles.social} onClick={signIn}>
              <div className={styles.icon}>
                <GoogleIcon />
              </div>
              <div className={styles.text}>Login with Google</div>
            </div>
            <div
              className={styles.viewError}
              style={{
                visibility: error ? "visible" : "hidden",
              }}
            />
          </form>
        </div>
      </div>
    </main>
  );
}

export default Login;
